<!--
  ClientPicker.vue -- searchable picker used inside ClientInfoGateModal.

  Lists existing Clients (under the active Distributor) with a recent-
  first default ordering. Selecting a row emits ``select`` with the
  Client id; the parent (the gate modal) closes itself and patches the
  Store's ``client_id`` FK.

  Search matches company + contact + email + phone -- same surface as the workspace
  list page, but no store_names here (the picker scope is "pick the
  Client that owns this Store" -- knowing the Store name is the
  question the picker answers, so searching by it would be circular).
-->
<template>
    <div class="cp" data-testid="client-picker">
        <input
            v-model="query"
            type="search"
            class="cp__search"
            placeholder="Search clients by company, contact, email, or phone"
            data-testid="client-picker-search"
        />
        <div
            v-if="store.loading"
            class="cp__loading"
            data-testid="client-picker-loading"
        >
            Loading clients...
        </div>
        <p
            v-else-if="visibleClients.length === 0 && totalCount === 0"
            class="cp__empty"
            data-testid="client-picker-empty"
        >
            No clients to pick from yet.
        </p>
        <p
            v-else-if="visibleClients.length === 0"
            class="cp__empty"
            data-testid="client-picker-no-match"
        >
            No clients match {{ query }}.
        </p>
        <ul v-else class="cp__list" data-testid="client-picker-list">
            <li
                v-for="row in visibleClients"
                :key="row.id"
                class="cp__item"
            >
                <!-- The row for the client ALREADY linked to this store is
                     marked (GH #4425): the list is every client of the
                     distributor, so without the marker nothing said which
                     one this store uses. At most one row is current, so the
                     marker testid carries no id; tests scope it to its row. -->
                <button
                    type="button"
                    class="cp__row"
                    :class="{ 'cp__row--current': isCurrent(row) }"
                    :aria-current="isCurrent(row) ? 'true' : undefined"
                    :data-testid="`client-picker-row-${row.id}`"
                    @click="$emit('select', row.id)"
                >
                    <!-- Labeled by company (card #499): the label is the
                         server's `display_name` (company, else contact
                         person), printed as sent. -->
                    <span
                        class="cp__name"
                        :data-testid="`client-picker-name-${row.id}`"
                    >
                        {{ label(row) }}
                        <span
                            v-if="isCurrent(row)"
                            class="cp__current"
                            data-testid="client-picker-current"
                        >
                            This store's client
                        </span>
                    </span>
                    <span
                        v-if="contactLine(row)"
                        class="cp__contact"
                        :data-testid="`client-picker-contact-${row.id}`"
                    >
                        {{ contactLine(row) }}
                    </span>
                </button>
            </li>
        </ul>
    </div>
</template>

<script>
import { useClientsWorkspaceStore } from '@/stores/clientsWorkspace'

export default {
    name: 'ClientPicker',
    emits: ['select'],
    props: {
        // The store's distributor. Sent to the list endpoint so a Super
        // Admin who is not impersonating sees only that distributor's
        // clients (the server enforces it; this only names the scope).
        distributorId: {
            type: [Number, String],
            default: null,
        },
        // The client currently linked to the store being edited, or null.
        // Ids are compared by value: the server sends numbers, a caller may
        // hold a string.
        currentClientId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return { query: '' }
    },
    computed: {
        store() {
            return useClientsWorkspaceStore()
        },
        totalCount() {
            return this.store.clients.length
        },
        visibleClients() {
            // Sort by recent activity first (most-recent on top -- the
            // Distributor probably picks a Client they're currently
            // collaborating with), then by name asc.
            const q = (this.query || '').trim().toLowerCase()
            const rows = this.store.clients.filter((r) => {
                if (!q) return true
                const hay = [r.display_name, r.name, r.contact_name, r.email, r.phone]
                    .filter(Boolean)
                    .map((s) => String(s).toLowerCase())
                    .join('\n')
                return hay.includes(q)
            })
            rows.sort((a, b) => {
                const ta = a.last_activity_at
                    ? Date.parse(a.last_activity_at)
                    : 0
                const tb = b.last_activity_at
                    ? Date.parse(b.last_activity_at)
                    : 0
                if (tb !== ta) return tb - ta
                return String(a.name || '').localeCompare(String(b.name || ''))
            })
            return rows.slice(0, 20)
        },
    },
    methods: {
        // The server's label; `name` only for a payload that predates it.
        label(row) {
            return row.display_name || row.name
        },
        // The person (when it is not already the label) and the email.
        contactLine(row) {
            const person =
                row.contact_name && row.contact_name !== this.label(row)
                    ? row.contact_name
                    : ''
            return [person, row.email].filter(Boolean).join(' - ')
        },
        isCurrent(row) {
            if (this.currentClientId == null || row == null) return false
            return String(row.id) === String(this.currentClientId)
        },
    },
    mounted() {
        // A named distributor always refetches: the shared list may hold
        // another scope's rows.
        if (this.distributorId != null || this.totalCount === 0) {
            this.store.fetchClients(this.distributorId)
        }
    },
}
</script>

<style scoped>
.cp__search {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid var(--tf-border, #d0d4dc);
    border-radius: var(--tf-radius-xs, 4px);
    font-size: 14px;
    margin-bottom: 12px;
}
.cp__loading,
.cp__empty {
    padding: 12px;
    color: var(--tf-fg-2);
    font-size: 13px;
    text-align: center;
}
.cp__list {
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 320px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.cp__row {
    width: 100%;
    text-align: left;
    background: var(--tf-surface-alt, #f7f8fa);
    border: 1px solid transparent;
    border-radius: var(--tf-radius-xs, 4px);
    padding: 10px 12px;
    font-family: inherit;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.cp__row:hover {
    background: var(--tf-bg, #fff);
    border-color: var(--tf-border, #d0d4dc);
}
.cp__row--current {
    border-color: var(--tf-primary, #119ae9);
    background: var(--tf-bg, #fff);
}
.cp__current {
    margin-left: 8px;
    padding: 1px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    color: var(--tf-primary, #119ae9);
    border: 1px solid currentColor;
}
.cp__name {
    font-weight: 500;
    color: var(--tf-fg-1);
    font-size: 14px;
}
.cp__contact {
    color: var(--tf-fg-3, #6b7280);
    font-size: 12px;
}
</style>
